import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import MKBox from "components/MKBox";

export default function ScrollReveal({
  children,
  delay,
  distance,
  duration,
  direction,
  threshold,
  sx,
}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) return undefined;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion || !("IntersectionObserver" in window)) {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: "0px 0px -8% 0px",
        threshold,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold]);

  const axis = direction === "left" || direction === "right" ? "X" : "Y";
  const sign = direction === "up" || direction === "left" ? 1 : -1;
  const offset = `${sign * distance}px`;

  return (
    <MKBox
      ref={ref}
      sx={[
        {
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translate3d(0, 0, 0)" : `translate${axis}(${offset})`,
          transition: `opacity ${duration}ms ease, transform ${duration}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
          transitionDelay: `${delay}ms`,
          willChange: "opacity, transform",
        },
        sx,
      ]}
    >
      {children}
    </MKBox>
  );
}

ScrollReveal.defaultProps = {
  delay: 0,
  distance: 28,
  duration: 650,
  direction: "up",
  threshold: 0.16,
  sx: undefined,
};

ScrollReveal.propTypes = {
  children: PropTypes.node.isRequired,
  delay: PropTypes.number,
  distance: PropTypes.number,
  duration: PropTypes.number,
  direction: PropTypes.oneOf(["up", "down", "left", "right"]),
  threshold: PropTypes.number,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};
